import { useState } from "react"
import { CopyIcon, Trash2Icon } from "lucide-react"
import { EditableLabel } from "@/components/editable-text/editable-label"
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { flowTitle, slugify } from "./text"

interface Props {
  playbook: Playbook
  flow: string
  onChange: (playbook: Playbook) => void
  onSelectFlow: (flow: string) => void
}

function retarget<T extends PlaybookOutcome | undefined>(outcome: T, from: string, to: string): T {
  return outcome?.type === "goto" && outcome.flow === from ? { ...outcome, flow: to } : outcome
}

function duplicateFlow(playbook: Playbook, from: string): [Playbook, string] {
  const title = `${flowTitle(playbook.flows, from)} copy`
  let key = slugify(title)
  for (let n = 2; playbook.flows[key]; n++) key = `${slugify(title)}_${n}`
  const items = structuredClone(playbook.flows[from].items).map(item => ({
    ...item,
    options: item.options?.map(o => ({ ...o, action: retarget(o.action, from, key) })),
    transitions: item.transitions?.map(t => ({ ...t, action: retarget(t.action, from, key) })),
  }))
  return [{ ...playbook, flows: { ...playbook.flows, [key]: { ...playbook.flows[from], title, items } } }, key]
}

export function FlowSheet({ playbook, flow, onChange, onSelectFlow }: Props) {
  const [confirmOpen, setConfirmOpen] = useState(false)
  const title = flowTitle(playbook.flows, flow)

  function rename(next: string) {
    if (next.trim()) onChange({ ...playbook, flows: { ...playbook.flows, [flow]: { ...playbook.flows[flow], title: next.trim() } } })
  }

  function duplicate() {
    const [next, key] = duplicateFlow(playbook, flow)
    onChange(next)
    onSelectFlow(key)
  }

  function remove() {
    const { [flow]: _, ...flows } = playbook.flows
    onSelectFlow("main")
    onChange({ ...playbook, flows: flows as Playbook["flows"] })
  }

  return <div className="flex items-center gap-1 rounded-lg border bg-card px-4 py-2 shrink-0">
    {flow === "main" ? <h2 className="flex-1 truncate text-lg font-semibold">{title}</h2>
      : <EditableLabel value={title} onChange={rename} placeholder="Untitled flow" className="flex-1 truncate text-lg font-semibold" />}
    <Button variant="ghost" size="icon-sm" aria-label="Duplicate flow" title="Duplicate this flow" onClick={duplicate}><CopyIcon /></Button>
    {flow !== "main" && <Button variant="ghost" size="icon-sm" aria-label="Delete flow" title="Delete this flow" onClick={() => setConfirmOpen(true)}><Trash2Icon /></Button>}

    <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete flow {title}?</AlertDialogTitle>
          <AlertDialogDescription>Its {playbook.flows[flow]?.items.length ?? 0} items will be removed. Any goto that points at it will fall to on_lost.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={remove}>Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </div>
}
